import {
  startTransition,
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  buildTaxonomyFormData,
  isSameTaxonomySelection,
  type EntityTaxonomySelection,
} from "@/core/services/types/taxonomy.types";

type TaxonomyUpdateResult =
  | { success: true }
  | { success: false; error: string };

type TaxonomyUpdateAction = (
  prevState: TaxonomyUpdateResult,
  formData: FormData,
) => Promise<TaxonomyUpdateResult>;

const initialState: TaxonomyUpdateResult = { success: false, error: "" };

interface UseTaxonomyAutoSaveOptions {
  entityId: string;
  taxonomy: EntityTaxonomySelection;
  updateAction: TaxonomyUpdateAction;
  onSuccess?: () => void;
}

export function useTaxonomyAutoSave({
  entityId,
  taxonomy,
  updateAction,
  onSuccess,
}: UseTaxonomyAutoSaveOptions) {
  const [state, formAction, isPending] = useActionState(
    updateAction,
    initialState,
  );
  const savedTaxonomyRef = useRef(taxonomy);
  const lastSavedStateRef = useRef(state);
  const [selection, setSelection] = useState(taxonomy);

  useEffect(() => {
    setSelection(taxonomy);
    savedTaxonomyRef.current = taxonomy;
  }, [taxonomy]);

  useEffect(() => {
    if (!state.success || state === lastSavedStateRef.current) return;
    lastSavedStateRef.current = state;
    savedTaxonomyRef.current = selection;
    onSuccess?.();
  }, [state, selection, onSuccess]);

  const submitSelection = useCallback(
    (next: EntityTaxonomySelection) => {
      if (isSameTaxonomySelection(next, savedTaxonomyRef.current)) return;
      startTransition(() => {
        formAction(buildTaxonomyFormData(entityId, next));
      });
    },
    [entityId, formAction],
  );

  const setArtFormIds = useCallback(
    (artFormIds: string[]) => {
      const next = { ...selection, artFormIds };
      setSelection(next);
      submitSelection(next);
    },
    [selection, submitSelection],
  );

  const setArtisticPeriodIds = useCallback(
    (artisticPeriodIds: string[]) => {
      const next = { ...selection, artisticPeriodIds };
      setSelection(next);
      submitSelection(next);
    },
    [selection, submitSelection],
  );

  const setCulturalAreaIds = useCallback(
    (culturalAreaIds: string[]) => {
      const next = { ...selection, culturalAreaIds };
      setSelection(next);
      submitSelection(next);
    },
    [selection, submitSelection],
  );

  const setExhibitionTypeIds = useCallback(
    (exhibitionTypeIds: string[]) => {
      const next = { ...selection, exhibitionTypeIds };
      setSelection(next);
      submitSelection(next);
    },
    [selection, submitSelection],
  );

  return {
    selection,
    setArtFormIds,
    setArtisticPeriodIds,
    setCulturalAreaIds,
    setExhibitionTypeIds,
    state,
    isPending,
  };
}
